import { useState, type FunctionComponent } from "react";
import { Link } from "react-router-dom";
import {
	PostStatus,
	type IPost,
} from "../../../../types/models/post.models.types";
import { Button } from "../../../../components/ui/button";
import { CampaignPostActionBrandPartial } from "./campaign-post-action.brand.partials";
import { CampaignPostMediaBrandPartial } from "./campaign-post-media.brand.partials";
import { DateTime } from "luxon";
import PostMetric from "../../../../components/post-metrics.components";
import { useUserStore } from "../../../../store/user.store";
import { useToast } from "../../../../hooks/use-toast";
import { useChangePostVisibilityEphemeralHttp } from "../../../../hooks/http/ephemeral/posts/change-post-visibility.ephemeral.http";
import { handleHttpError } from "../../../../utils/utils";
import Spinner from "../../../../components/spinner.components";
import { Checkbox } from "../../../../components/ui/checkbox";
import { AccountType } from "../../../../constants/account-types.constants";

type AppProps = {
	post: IPost;
};

export const CampaignPostBrandPartial: FunctionComponent<AppProps> = ({
	post,
}) => {
	const { user } = useUserStore();
	const { toast } = useToast();
	const [visibleToBrand, setVisibleToBrand] = useState(post.visibleToBrand);
	const {
		execute: changePostVisibilityHttp,
		fetching: fetchingChangePostVisibility,
	} = useChangePostVisibilityEphemeralHttp();

	async function handlePostVisibility() {
		const response = await changePostVisibilityHttp({
			body: {
				visibleToBrand: (!visibleToBrand).toString(),
			},
			pathParams: {
				id: post.id,
			},
		});
		if (!response.success) {
			return handleHttpError(response, toast);
		}
		setVisibleToBrand(!visibleToBrand);
	}
	function handlePostStatus() {
		if (post.postStatus.status === PostStatus.postApproved) {
			return "Live post";
		}
		if (post.postStatus.status === PostStatus.flagged) {
			return "Live posts: flagged";
		}
		if (post.postStatus.status === PostStatus.flaggedReview) {
			return "Live posts: flagged review";
		}
		if (post.postStatus.status === PostStatus.disqualify) {
			return "Live posts: disqualified";
		}
		if (post.postStatus.status === PostStatus.post) {
			return "Post submission: pending";
		}
		if (post.postStatus.status === PostStatus.postRejected) {
			return "Post submission: rejected";
		}
		if (post.postStatus.status === PostStatus.draft) {
			return "Draft submission: pending";
		}
		if (post.postStatus.status === PostStatus.draftApproved) {
			return "Draft submission: approved";
		}
		if (post.postStatus.status === PostStatus.draftRejected) {
			return "Draft submission: rejected";
		}
		return "";
	}

	return (
		<div className="flex flex-col gap-2">
			{post.postStatus.status === "flagged" && (
				<div className="flex items-start gap-1 px-3 py-4 border border-border rounded-lg font-semibold text-secondary">
					Reason:
					<span className="max-h-12 text-white text-wrap truncate">
						{post.postStatus.message}
					</span>
				</div>
      )}
			{post.postStatus.status === PostStatus.postApproved && (
        <PostMetric post={post} />
      )}
			<div className="flex flex-col border border-border rounded-lg w-full">
				<div className="flex justify-between items-center px-3 pt-4 w-full">
					<div className="flex flex-col font-semibold text-left text-primary">
						<p className="text-base">{post.creator?.name}</p>
						{post.postStatus.status === PostStatus.draft ? (
							<span className="text-secondary text-xs">
								Submitted on{" "}
								{DateTime.fromISO(
									new Date(post.postStatus.timestamp).toISOString(),
								).toRelative()}
							</span>
						) : (
							<span className="text-secondary text-xs">
								{DateTime.fromISO(
									new Date(post.postStatus.timestamp).toISOString(),
								).toRelative()}
							</span>
						)}
					</div>
					<Link
						target="_blank"
						to={`https://instagram.com/${post.social?.handle}`}
					>
						<Button className="bg-tertiarybuttonbg px-4 py-2 border border-border font-semibold">
							View profile
						</Button>
					</Link>
				</div>
				<div className="py-3 p-2">
					<Button
						className={`bg-tertiarybuttonbg px-4 border border-border tracking-wide h-8 font-semibold text-xs ${window.location.pathname.includes("search") ? "flex" : "hidden"}`}
					>
						{handlePostStatus()}
					</Button>
				</div>
				<CampaignPostMediaBrandPartial post={post} />
			</div>

			<div className="flex flex-col gap-4 mt-2">
				{user?.accountType === AccountType.EPHEMERAL && (post.postStatus?.status === PostStatus.draft || post.postStatus?.status === PostStatus.draftApproved) && user?.visibleToBrandEnabled === false && (
					<div className="flex items-center gap-2">
						{fetchingChangePostVisibility ? (
							<Spinner width={16} />
						) : (
							<Checkbox
								className="border border-border"
								checked={visibleToBrand}
								onClick={() => {
									handlePostVisibility();
								}}
							/>
						)}

						<span className="font-semibold text-base text-primary cursor-pointer">
							Show to brand
						</span>
					</div>
				)}
			</div>

			<CampaignPostActionBrandPartial post={post} />
		</div>
	);
};
